import React, { useMemo, useState } from "react";
import { isEmpty } from "lodash";

import {
  CREATE_BOOKING,
  FORMAT_DATE,
  MAX_GUEST_LIST,
  MEETING_INFO,
} from "../../../../../helpers";
import { IMG } from "../../../../../themes";

import { MeetingDetailStyled } from "./styles";
import { momentDateByLanguage } from "../../../../../helpers/functions/booking";
import CopyIcon from "../../../../../components/CopyIcon";
import ToastCopy from "../../../../../components/ToastCopy";
import ExternalIcon from "../../../../../components/ExternalIcon";

function GuestList({ guests, language, colorTheme }) {
  const [viewAllGuest, setViewAllGuest] = useState(false);

  const toggleViewAllGuestBtn = () => {
    setViewAllGuest((prev) => (prev = !prev));
  };

  const shownGuestsByAmount = (
    guests,
    amount = guests.length,
    viewAllGuest,
  ) => {
    const shownGuests = viewAllGuest ? guests : guests.slice(0, amount);

    return shownGuests;
  };

  const guestList = useMemo(() => {
    return (
      <div className="meeting-info__box__row__guest__box">
        {shownGuestsByAmount(guests, MAX_GUEST_LIST, viewAllGuest).map(
          (guest) => {
            return (
              <p key={guest.id} className="meeting-info__box__row__guest__item">
                {guest.name ? guest.name : guest.username}
              </p>
            );
          },
        )}
        {guests.length > MAX_GUEST_LIST && (
          <button
            onClick={toggleViewAllGuestBtn}
            className="meeting-info__box__row__guest__view-all-button"
          >
            {viewAllGuest
              ? MEETING_INFO.SEE_LESS_GUEST[language]
              : MEETING_INFO.VIEW_ALL_GUEST[language]}
          </button>
        )}
      </div>
    );
  }, [guests, viewAllGuest, language]);

  return guestList;
}

export default function MeetingDetail({
  bookingDetail,
  language,
  colorTheme,
  handleClickCopy,
  closeToast,
  isClickCopy,
}) {
  let isMeetingLink = null;
  if (bookingDetail.meetingLink) {
    isMeetingLink = bookingDetail.meetingLink.trim().length;
  }
  const withProtocol = (url) =>
    url && url.match(/^https?:\/\//) ? url : `http://${url}`;
  return (
    <MeetingDetailStyled colorTheme={colorTheme}>
      <div className="meeting-info__box__row">
        <p className="meeting-info__box__row__title">
          <span className="meeting-info__box__row__title__icon">
            <img src={IMG["user-meeting-info.svg"]} alt="user-meeting-info" />
          </span>
          <span>{MEETING_INFO.MEETING_OWNER[language]}</span>
        </p>
        <p className="meeting-info__box__row__detail">
          {bookingDetail.owner.name}
        </p>
      </div>

      <div className="meeting-info__box__row">
        <p className="meeting-info__box__row__title">
          <span className="meeting-info__box__row__title__icon">
            <img src={IMG["date-meeting-info.svg"]} alt="date-meeting-info" />
          </span>
          <span>{MEETING_INFO.DATE[language]}</span>
        </p>
        <p className="meeting-info__box__row__detail">
          {momentDateByLanguage(
            bookingDetail.date,
            language,
            FORMAT_DATE.DATE_WITH_DAY,
          )}
        </p>
      </div>

      <div className="meeting-info__box__row">
        <p className="meeting-info__box__row__title">
          <span className="meeting-info__box__row__title__icon">
            <img
              src={IMG["duration-meeting-info.svg"]}
              alt="duration-meeting-info"
            />
          </span>
          <span>{MEETING_INFO.DURATION[language]}</span>
        </p>
        <p className="meeting-info__box__row__detail">
          {bookingDetail.duration}
        </p>
      </div>

      <div className="meeting-info__box__row">
        <p className="meeting-info__box__row__title">
          <span className="meeting-info__box__row__title__icon">
            <img
              src={IMG["description-meeting-info.svg"]}
              alt="description-meeting-info"
            />
          </span>
          <span>{MEETING_INFO.DESCRIPTION[language]}</span>
        </p>
        <p className="meeting-info__box__row__detail">
          {bookingDetail.description}
        </p>
      </div>

      {bookingDetail.controlRoomManualUrl && (
        <div className="meeting-info__box__row">
          <p className="meeting-info__box__row__title">
            <span className="meeting-info__box__row__title__icon">
              <img
                src={IMG["ic_info-outline.svg"]}
                alt="room-manual"
                width={16}
                height={16}
              />
            </span>
            <span>{MEETING_INFO.ROOM_MANUAL[language]}</span>
          </p>
          <a
            className="meeting-info__box__row__detail link-icon"
            href={withProtocol(bookingDetail.controlRoomManualUrl)}
            target="_blank"
            style={{ textDecoration: "underline", color: colorTheme.MAIN }}
          >
            {CREATE_BOOKING.USER_MANUAL[language]}
          </a>
        </div>
      )}

      {bookingDetail.websiteUrl && (
        <div className="meeting-info__box__row">
          <a
            className="link-icon"
            href={withProtocol(bookingDetail.websiteUrl)}
            target="_blank"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: colorTheme.MAIN,
            }}
          >
            <span className="meeting-info__box__row__title__icon">
              <ExternalIcon color={colorTheme.MAIN} width={16} height={16} />
            </span>
            <span style={{ textDecoration: "underline" }}>
              {MEETING_INFO.CONTROL_ROOM[language]}
            </span>
          </a>
        </div>
      )}

      {isMeetingLink && (
        <div className="meeting-info__box__row">
          <p className="meeting-info__box__row__title">
            <span className="meeting-info__box__row__title__icon">
              <img src={IMG["link.svg"]} alt="link" />
            </span>
            <span>{MEETING_INFO.MEETING_LINK[language]}</span>
          </p>
          <div className="two-lines">
            <a
              className="link-icon"
              href={bookingDetail.meetingLink}
              target="_blank"
            >
              <p className="text-inline">{bookingDetail.meetingLink}</p>
            </a>
            <button
              className="copy-link-button"
              onClick={() => handleClickCopy(bookingDetail.meetingLink)}
            >
              <CopyIcon color={colorTheme.MAIN} />
            </button>
          </div>
        </div>
      )}

      <ToastCopy
        isOpenToast={isClickCopy}
        onClickCloseToast={() => closeToast()}
        language={language}
      />

      {!isEmpty(bookingDetail.guests) && (
        <div className="meeting-info__box__row__guest">
          <p className="meeting-info__box__row__title">
            <span className="meeting-info__box__row__title__icon">
              <img
                src={IMG["guest-meeting-info.svg"]}
                alt="guest-meeting-info"
              />
            </span>
            <span>{MEETING_INFO.GUESTS[language]}</span>
          </p>
          <div>
            <GuestList
              guests={bookingDetail.guests}
              language={language}
              colorTheme={colorTheme}
            />
          </div>
        </div>
      )}
    </MeetingDetailStyled>
  );
}
